// ============================================================
// MASH machine_card viewer — hardcoded contract constants + pure helpers
// (ME-MACHINEID-RENDER SP-2). No JSX / React here.
// ============================================================
// SP-0 re-pin (ME-CONTRACT-SYNC-7TO6) is DONE, so we hardcode the SIX-section
// label map + fixed order + the `arm` deep-link target against the stable
// contract. The RENDERER STILL iterates `data.tabs[]` by `part_slug`
// (tab-list-agnostic, GAP-9) — this map only supplies display labels + tab
// order, so a stray/unknown slug still renders (falls back to its own `name`).

import type {
  EvidenceTab,
  MachineCardArtifactData,
  VerdictTone,
  WindowKey,
} from "../../../../../shared/components/machineEvidence";
import { resolveVerdict, isPlaceholderTab } from "../../../../../shared/components/machineEvidence";

/** Deep-link target tab for the merged Picker+Jaws section (§7 Q1 / SP-0).
 *  `machine_evidence.links_to.machine_card_tab` == "arm" for MASH. Hardcoded. */
export const MACHINE_CARD_ARM_TAB_SLUG = "arm";

/** Fixed display order of the six overview sections (arm = Picker+Jaws merged). */
export const SIX_TAB_ORDER: readonly string[] = [
  "io_master",
  "feeders",
  "arm",
  "unloader",
  "vision",
  "control_safety",
];

/** Hardcoded six-tab label map (display only — the renderer stays tab-agnostic). */
export const SIX_TAB_LABELS: Record<string, string> = {
  io_master: "Output",
  feeders: "Feeders",
  arm: "Arm",
  unloader: "Trays / Unloader",
  vision: "Vision",
  control_safety: "Control / Safety",
};

/** Display labels for the five per-window pills, keyed by the closed `WindowKey`
 *  enum (SP-1b — the FUNCTIONAL selector; the pill ROW iterates `WINDOW_KEYS` for
 *  its fixed order). English defaults — SP-6 wraps these in i18n. */
export const WINDOW_PILL_LABELS: Record<WindowKey, string> = {
  last_hour: "Last hour",
  this_shift: "This shift",
  today: "Today",
  this_week: "This week",
  all_time: "All time",
};

/** GAP-6 — Arm angles/positions flow in telemetry but are NOT emitted as card
 *  figures. Render the "also measured, not shown yet" slot as signal-key labels
 *  ONLY (no numbers). */
export const ARM_ALSO_MEASURED_KEYS: readonly string[] = [
  "orient_angle",
  "achieved_angle_deg",
  "pick_pos_x_mm",
  "pick_pos_y_mm",
];

/** GAP-2 — the two physical tray slots known unused on both blisters (provisional
 *  fallback until the bot ships the authoritative 6-of-8 position map). */
export const TRAY_UNUSED_SLOTS: readonly string[] = ["2_1", "2_2"];

/** Human label for a tab: hardcoded six-tab map first, else the tab's own name,
 *  else the raw slug (a stray/unknown slug still renders). */
export const tabLabel = (tab: EvidenceTab): string =>
  SIX_TAB_LABELS[tab.part_slug] || tab.name || tab.part_slug;

/** Order the card's tabs by the fixed six-tab order; unknown slugs are appended
 *  in their original order (tab-list-agnostic — never drops a stray tab). */
export const orderedSectionTabs = (data: MachineCardArtifactData): EvidenceTab[] => {
  const tabs = data.tabs ?? [];
  const known = SIX_TAB_ORDER
    .map((slug) => tabs.find((t) => t.part_slug === slug))
    .filter((t): t is EvidenceTab => !!t);
  const unknown = tabs.filter((t) => !SIX_TAB_ORDER.includes(t.part_slug));
  return [...known, ...unknown];
};

const TONE_SEVERITY: Record<VerdictTone, number> = {
  critical: 3,
  warning: 2,
  neutral: 1,
  positive: 0,
};

/** True when the card is an OVERVIEW roll-up (one tab per section) vs a single
 *  per-part card (exactly one populated tab). */
export const isOverviewCard = (data: MachineCardArtifactData): boolean =>
  (data.tabs?.length ?? 0) > 1;

/** The POPULATED tab with the worst (most severe) verdict across the card.
 *  Placeholder tabs (no figures + unknown verdict) are ignored so an all-pending
 *  overview does not claim a fault. Ties resolve to the first in section order.
 *  Returns null when every tab is a placeholder. */
export const worstVerdictTab = (data: MachineCardArtifactData): EvidenceTab | null => {
  let best: EvidenceTab | null = null;
  let bestSeverity = -1;
  for (const tab of orderedSectionTabs(data)) {
    if (isPlaceholderTab(tab)) continue;
    const severity = TONE_SEVERITY[resolveVerdict(tab.verdict).tone];
    if (severity > bestSeverity) {
      best = tab;
      bestSeverity = severity;
    }
  }
  return best;
};
